import React from "react";
import { Helmet } from "react-helmet";
import { isSearchIndexingBlocked } from "../../config/searchIndexing";
import { buildCanonicalUrl } from "../canonicalUrl";
import { serializeJsonLdForHtmlScript } from "../serializeJsonLd";
import { DEFAULT_OG_IMAGE_PATH, SITE_NAME } from "../siteConstants";
import type { IResolvedPageSEO } from "../types/core";

export type StandardPageHelmetProps = {
  seo: IResolvedPageSEO;
  /** Full page URL for og:url; defaults to `window.location.href` in the browser. */
  pageUrl?: string;
  /** Absolute image URL for og:image / twitter:image. */
  ogImageUrl?: string;
  /** Overrides Open Graph type from the resolved SEO (e.g. profile routes). */
  openGraphTypeOverride?: string;
  /** Structured data; omitted when search indexing is blocked. */
  jsonLd?: unknown | null;
};

export default function StandardPageHelmet({
  seo,
  pageUrl,
  ogImageUrl,
  openGraphTypeOverride,
  jsonLd,
}: StandardPageHelmetProps) {
  const url =
    pageUrl ??
    (typeof window !== "undefined" ? window.location.href : "");
  const canonical = buildCanonicalUrl(url, {
    dropTrackingParams: seo.canonicalDropTrackingParams,
  });
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  const defaultImage = `${origin.replace(/\/+$/, "")}${DEFAULT_OG_IMAGE_PATH}`;
  const image = ogImageUrl || defaultImage;
  const ogType = openGraphTypeOverride ?? seo.openGraphType;
  const emitJsonLd = Boolean(jsonLd) && !isSearchIndexingBlocked();

  return (
    <Helmet>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <link rel="canonical" href={canonical} />

      <meta name="robots" content={seo.robots} />
      <meta name="googlebot" content={seo.robots} />

      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={SITE_NAME} />

      <meta name="twitter:card" content={seo.twitterCard} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={SITE_NAME} />

      {emitJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: serializeJsonLdForHtmlScript(jsonLd),
          }}
        />
      )}
    </Helmet>
  );
}
